// Project cost ranges derived from the internal 2026 rate card (blended team
// day rate ≈ 1,150–1,250 € net). Ranges are estimates, never quotes.
export type Lang = "en" | "de" | "el";

type Tier = { id: string; days: [number, number]; price: [number, number]; weeks: [number, number] };

export const TIERS: Tier[] = [
  { id: "ai", days: [12, 30], price: [15, 35], weeks: [3, 8] },
  { id: "internal", days: [12, 22], price: [15, 28], weeks: [3, 6] },
  { id: "portal", days: [25, 45], price: [30, 55], weeks: [6, 10] },
  { id: "crm", days: [30, 60], price: [35, 70], weeks: [8, 14] },
  { id: "saas", days: [40, 80], price: [50, 95], weeks: [10, 16] },
];

type Copy = {
  metaTitle: string;
  metaDescription: string;
  eyebrow: string;
  title: string;
  titleAccent: string;
  subtitle: string;
  home: string;
  tableKicker: string;
  tableTitle: string;
  colType: string;
  colPrice: string;
  colTime: string;
  colEffort: string;
  days: string;
  weeks: string;
  from: string;
  tiers: Record<string, { name: string; desc: string }>;
  note: string;
  driversKicker: string;
  driversTitle: string;
  drivers: { title: string; body: string }[];
  ctaTitle: string;
  ctaBody: string;
  ctaButton: string;
  faqKicker: string;
  faqTitle: string;
  faqs: { q: string; a: string }[];
};

export const PRICING_COPY: Record<Lang, Copy> = {
  de: {
    metaTitle: "Was kostet individuelle Software? Preise 2026 | DevCore",
    metaDescription:
      "Realistische Preisspannen für Kundenportal, CRM, SaaS-MVP, interne Tools und KI-Lösungen – mit Zeitrahmen und den Faktoren, die den Preis bestimmen.",
    eyebrow: "Kosten",
    title: "Was kostet",
    titleAccent: "individuelle Software?",
    subtitle:
      "Ehrliche Preisspannen aus echten Projekten – damit du weißt, womit du rechnen kannst, bevor du mit uns sprichst.",
    home: "Start",
    tableKicker: "// Preisspannen 2026",
    tableTitle: "Typische Projekte und was sie kosten",
    colType: "Projektart",
    colPrice: "Budget (netto)",
    colTime: "Zeitrahmen",
    colEffort: "Aufwand",
    days: "Personentage",
    weeks: "Wochen",
    from: "ab",
    tiers: {
      ai: { name: "KI-Tool & Automatisierung", desc: "z. B. Dokumente auswerten, Übersetzungen, Agent für interne Abläufe" },
      internal: { name: "Internes Tool / Dashboard", desc: "Ein klarer Ablauf, wenige Rollen, Anbindung an 1–2 bestehende Systeme" },
      portal: { name: "Kundenportal", desc: "Login, Rollen, Dokumente, Status-Tracking, E-Mail-Benachrichtigungen" },
      crm: { name: "Individuelles CRM", desc: "Pipeline, Kundenakte, Aufgaben, Auswertungen, Schnittstellen" },
      saas: { name: "SaaS-Plattform (MVP)", desc: "Mehrmandantenfähig, Abrechnung, Onboarding, Admin-Bereich" },
    },
    note:
      "Alle Angaben sind Schätzungen zzgl. MwSt. und kein Angebot. Einen verbindlichen Preis nennen wir erst, wenn Anforderungen, Abläufe und Schnittstellen in einer gemeinsamen Konzeptphase sauber geklärt sind – auf Wunsch dann als Festpreis.",
    driversKicker: "// Preistreiber",
    driversTitle: "Was den Preis nach oben oder unten bewegt",
    drivers: [
      { title: "Anzahl der Abläufe", body: "Jeder zusätzliche Workflow, jede Rolle und jede Sonderregel bringt Aufwand. Ein klarer Kernprozess ist am günstigsten." },
      { title: "Schnittstellen", body: "Anbindungen an ERP, Shop, Buchhaltung oder Alt-Systeme sind oft der größte Unbekannte – je dokumentierter, desto günstiger." },
      { title: "Daten-Migration", body: "Bestehende Daten sauber zu übernehmen kostet Zeit. Ein Neustart ohne Altlasten spart Budget." },
      { title: "Design-Anspruch", body: "Solide Oberfläche auf Basis eines Design-Systems oder komplett eigenes Markenerlebnis – beides geht, kostet aber unterschiedlich." },
    ],
    ctaTitle: "Deine genaue Schätzung in 2 Minuten",
    ctaBody: "Beschreib deine Idee im Blueprint-Generator. Du bekommst kostenlos einen Projektplan – und wir können dir danach einen konkreten Preis nennen.",
    ctaButton: "Kostenlosen Blueprint erstellen",
    faqKicker: "// Häufige Fragen",
    faqTitle: "Fragen zu Kosten",
    faqs: [
      { q: "Was kostet eine individuelle Web-App?", a: "Eine kleine Web-App mit einem klaren Ablauf liegt meist bei 15.000–28.000 € netto. Umfangreichere Plattformen wie ein Kundenportal oder CRM liegen zwischen 30.000 und 70.000 €, ein SaaS-MVP bei 50.000–95.000 €." },
      { q: "Bietet ihr Festpreise an?", a: "Ja – aber erst, wenn der Umfang wirklich feststeht. Dafür klären wir in einer gemeinsamen Konzeptphase Anforderungen, Abläufe, Rollen und Schnittstellen im Detail. Auf dieser Grundlage kalkulieren wir einen belastbaren Festpreis. Der Blueprint ist dafür ein guter Startpunkt, ersetzt diese Phase aber nicht." },
      { q: "Warum eigene Software statt SaaS-Abo?", a: "Weil du unabhängig bleibst. Der Code gehört dir, du entscheidest über Funktionen, Daten und Weiterentwicklung – nicht ein Anbieter mit seiner Roadmap, seinen Preisänderungen oder seinen Nutzungsbedingungen. Deine Software passt sich deinem Prozess an, nicht umgekehrt." },
      { q: "Wie lange dauert die Entwicklung?", a: "Kleine Tools sind in 3–6 Wochen live, Portale und CRMs in 6–14 Wochen, ein SaaS-MVP in 10–16 Wochen." },
      { q: "Was kostet der Betrieb danach?", a: "Hosting ist meist zweistellig pro Monat. Wartung und Weiterentwicklung rechnen wir nach tatsächlichem Aufwand ab oder über ein festes Monatsbudget." },
    ],
  },
  en: {
    metaTitle: "How much does custom software cost? 2026 prices | DevCore",
    metaDescription:
      "Realistic price ranges for customer portals, CRMs, SaaS MVPs, internal tools and AI solutions – with timelines and the factors that drive cost.",
    eyebrow: "Pricing",
    title: "How much does",
    titleAccent: "custom software cost?",
    subtitle: "Honest price ranges from real projects – so you know what to expect before you talk to us.",
    home: "Home",
    tableKicker: "// Price ranges 2026",
    tableTitle: "Typical projects and what they cost",
    colType: "Project type",
    colPrice: "Budget (net)",
    colTime: "Timeline",
    colEffort: "Effort",
    days: "person-days",
    weeks: "weeks",
    from: "from",
    tiers: {
      ai: { name: "AI tool & automation", desc: "e.g. document analysis, translation, an agent for internal workflows" },
      internal: { name: "Internal tool / dashboard", desc: "One clear workflow, few roles, connected to 1–2 existing systems" },
      portal: { name: "Customer portal", desc: "Login, roles, documents, status tracking, email notifications" },
      crm: { name: "Custom CRM", desc: "Pipeline, customer records, tasks, reporting, integrations" },
      saas: { name: "SaaS platform (MVP)", desc: "Multi-tenant, billing, onboarding, admin area" },
    },
    note: "All figures are estimates excluding VAT, not a quote. We only give a binding price once requirements, workflows and integrations have been properly clarified in a joint concept phase – as a fixed price if you prefer.",
    driversKicker: "// Cost drivers",
    driversTitle: "What moves the price up or down",
    drivers: [
      { title: "Number of workflows", body: "Every extra workflow, role and special rule adds effort. One clear core process is the most affordable." },
      { title: "Integrations", body: "Connections to ERP, shop, accounting or legacy systems are often the biggest unknown – the better documented, the cheaper." },
      { title: "Data migration", body: "Moving existing data cleanly takes time. Starting fresh saves budget." },
      { title: "Design ambition", body: "A solid interface on a design system or a fully custom brand experience – both work, at different cost." },
    ],
    ctaTitle: "Your exact estimate in 2 minutes",
    ctaBody: "Describe your idea in the Blueprint generator. You get a free project plan – and we can then give you a concrete price.",
    ctaButton: "Create free Blueprint",
    faqKicker: "// FAQ",
    faqTitle: "Questions about cost",
    faqs: [
      { q: "How much does a custom web app cost?", a: "A small web app with one clear workflow usually costs €15,000–28,000 net. Larger platforms like a customer portal or CRM range from €30,000 to €70,000, a SaaS MVP €50,000–95,000." },
      { q: "Do you offer fixed prices?", a: "Yes – but only once the scope is truly settled. In a joint concept phase we clarify requirements, workflows, roles and integrations in detail, and calculate a reliable fixed price on that basis. The Blueprint is a good starting point, but it doesn't replace this phase." },
      { q: "Why your own software instead of a SaaS subscription?", a: "Because you stay independent. You own the code and decide on features, data and the roadmap – not a vendor with its own priorities, price changes or terms of service. Your software adapts to your process, not the other way round." },
      { q: "How long does development take?", a: "Small tools go live in 3–6 weeks, portals and CRMs in 6–14 weeks, a SaaS MVP in 10–16 weeks." },
      { q: "What does it cost to run afterwards?", a: "Hosting is usually double digits per month. Maintenance and new features are billed by actual effort or a fixed monthly budget." },
    ],
  },
  el: {
    metaTitle: "Πόσο κοστίζει το custom λογισμικό; Τιμές 2026 | DevCore",
    metaDescription:
      "Ρεαλιστικά εύρη τιμών για portal πελατών, CRM, SaaS MVP, εσωτερικά εργαλεία και λύσεις AI – με χρονοδιαγράμματα και τους παράγοντες που καθορίζουν το κόστος.",
    eyebrow: "Κόστος",
    title: "Πόσο κοστίζει",
    titleAccent: "το custom λογισμικό;",
    subtitle: "Ειλικρινή εύρη τιμών από πραγματικά έργα – για να ξέρεις τι να περιμένεις πριν μιλήσεις μαζί μας.",
    home: "Αρχική",
    tableKicker: "// Εύρη τιμών 2026",
    tableTitle: "Τυπικά έργα και το κόστος τους",
    colType: "Τύπος έργου",
    colPrice: "Budget (καθαρό)",
    colTime: "Χρόνος",
    colEffort: "Φόρτος",
    days: "ανθρωποημέρες",
    weeks: "εβδομάδες",
    from: "από",
    tiers: {
      ai: { name: "Εργαλείο AI & αυτοματοποίηση", desc: "π.χ. ανάλυση εγγράφων, μετάφραση, agent για εσωτερικές διαδικασίες" },
      internal: { name: "Εσωτερικό εργαλείο / dashboard", desc: "Μία σαφής διαδικασία, λίγοι ρόλοι, σύνδεση με 1–2 υπάρχοντα συστήματα" },
      portal: { name: "Portal πελατών", desc: "Σύνδεση, ρόλοι, έγγραφα, παρακολούθηση κατάστασης, ειδοποιήσεις email" },
      crm: { name: "Custom CRM", desc: "Pipeline, καρτέλα πελάτη, εργασίες, αναφορές, διασυνδέσεις" },
      saas: { name: "Πλατφόρμα SaaS (MVP)", desc: "Multi-tenant, χρεώσεις, onboarding, περιοχή διαχείρισης" },
    },
    note: "Όλα τα ποσά είναι εκτιμήσεις χωρίς ΦΠΑ και όχι προσφορά. Δεσμευτική τιμή δίνουμε μόνο όταν οι απαιτήσεις, οι διαδικασίες και οι διασυνδέσεις έχουν διευκρινιστεί σε μια κοινή φάση σχεδιασμού – και τότε, αν θέλεις, ως σταθερή τιμή.",
    driversKicker: "// Παράγοντες κόστους",
    driversTitle: "Τι ανεβάζει ή κατεβάζει την τιμή",
    drivers: [
      { title: "Πλήθος διαδικασιών", body: "Κάθε επιπλέον ροή, ρόλος και ειδικός κανόνας προσθέτει φόρτο. Μία σαφής βασική διαδικασία είναι η πιο οικονομική." },
      { title: "Διασυνδέσεις", body: "Συνδέσεις με ERP, eshop, λογιστικό ή παλιά συστήματα είναι συχνά ο μεγαλύτερος άγνωστος – όσο καλύτερη η τεκμηρίωση, τόσο φθηνότερα." },
      { title: "Μεταφορά δεδομένων", body: "Η σωστή μεταφορά υπαρχόντων δεδομένων θέλει χρόνο. Μια νέα αρχή εξοικονομεί budget." },
      { title: "Απαιτήσεις σχεδιασμού", body: "Σταθερό interface σε design system ή πλήρως custom εμπειρία brand – και τα δύο γίνονται, με διαφορετικό κόστος." },
    ],
    ctaTitle: "Η ακριβής εκτίμησή σου σε 2 λεπτά",
    ctaBody: "Περιέγραψε την ιδέα σου στον Blueprint generator. Παίρνεις δωρεάν πλάνο έργου – και μετά μπορούμε να σου δώσουμε συγκεκριμένη τιμή.",
    ctaButton: "Δωρεάν Blueprint",
    faqKicker: "// Συχνές ερωτήσεις",
    faqTitle: "Ερωτήσεις για το κόστος",
    faqs: [
      { q: "Πόσο κοστίζει μια custom web εφαρμογή;", a: "Μια μικρή web εφαρμογή με μία σαφή διαδικασία κοστίζει συνήθως 15.000–28.000 € καθαρά. Μεγαλύτερες πλατφόρμες όπως portal πελατών ή CRM κυμαίνονται 30.000–70.000 €, ένα SaaS MVP 50.000–95.000 €." },
      { q: "Προσφέρετε σταθερές τιμές;", a: "Ναι – αλλά μόνο όταν το εύρος έχει πραγματικά οριστεί. Σε μια κοινή φάση σχεδιασμού διευκρινίζουμε λεπτομερώς απαιτήσεις, διαδικασίες, ρόλους και διασυνδέσεις και με βάση αυτά υπολογίζουμε μια αξιόπιστη σταθερή τιμή. Το Blueprint είναι καλό σημείο εκκίνησης, αλλά δεν αντικαθιστά αυτή τη φάση." },
      { q: "Γιατί δικό σου λογισμικό αντί για συνδρομή SaaS;", a: "Γιατί παραμένεις ανεξάρτητος. Ο κώδικας σου ανήκει και εσύ αποφασίζεις για λειτουργίες, δεδομένα και εξέλιξη – όχι ένας πάροχος με τις δικές του προτεραιότητες, αλλαγές τιμών ή όρους χρήσης. Το λογισμικό προσαρμόζεται στη διαδικασία σου, όχι το αντίστροφο." },
      { q: "Πόσο διαρκεί η ανάπτυξη;", a: "Μικρά εργαλεία είναι έτοιμα σε 3–6 εβδομάδες, portals και CRM σε 6–14 εβδομάδες, ένα SaaS MVP σε 10–16 εβδομάδες." },
      { q: "Πόσο κοστίζει η λειτουργία μετά;", a: "Το hosting είναι συνήθως διψήφιο ποσό τον μήνα. Συντήρηση και νέες λειτουργίες χρεώνονται βάσει πραγματικού φόρτου ή σταθερού μηνιαίου budget." },
    ],
  },
};
